import type {PrimerComponentSubnav} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentSubnav'
import {useEffect, useRef, useState} from 'react'
import {AnchorNav} from '@primer/react-brand/esm'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {ArrowUpRightIcon} from '@primer/octicons-react'

import Styles from './Nav.module.css'

type Props = {
  content: PrimerComponentSubnav
  themeMap?: Record<string, string>
}

const DEFAULT_THEME_MAP: Record<string, string> = {}

export function Nav(props: Props) {
  const {content, themeMap = DEFAULT_THEME_MAP} = props

  const {links} = content.fields

  const sectionRef = useRef<HTMLElement>(null)
  const [activeTheme, setActiveTheme] = useState<string>()

  // Mobile theme
  // AnchorNav doesn't expose which link is current, but marks it with data-active. Watch that to theme the nav itself.
  useEffect(() => {
    const nav = sectionRef.current?.querySelector('nav')
    if (!nav) return

    const observer = new MutationObserver(() => {
      const theme = nav.querySelector('a[data-active="true"]')?.getAttribute('data-theme')
      // Keep the last theme while no link is active, matching the button label, which also stays put
      if (theme) setActiveTheme(theme)
    })
    observer.observe(nav, {subtree: true, attributes: true, attributeFilter: ['data-active']})

    return () => observer.disconnect()
  }, [])

  const firstSectionId = links
    .map(link => ('href' in link.fields ? link.fields.href : ''))
    .find(href => href.startsWith('#'))
    ?.slice(1)
  const navTheme = activeTheme ?? (firstSectionId ? themeMap[firstSectionId] : undefined)

  return (
    <section ref={sectionRef} className={Styles.Nav}>
      <AnchorNav {...(navTheme ? {'data-theme': navTheme} : {})}>
        {links.map(link => {
          if ('href' in link.fields) {
            const href = link.fields.href
            const analytics = getAnalyticsEvent({
              action: link.fields.text,
              tag: 'a',
              context: 'anchor_nav_link',
              location: 'anchor_nav',
            })

            // AnchorNav.Link only supports in-page anchors; real URLs must use AnchorNav.Action
            if (!href.startsWith('#')) {
              return (
                <AnchorNav.Action
                  key={link.sys.id}
                  href={href}
                  {...(link.fields.openInNewTab ? {target: '_blank', rel: 'noopener noreferrer'} : {})}
                  {...analytics}
                >
                  {link.fields.text}
                  <ArrowUpRightIcon className={Styles.Icon} />
                </AnchorNav.Action>
              )
            }

            // Extract the ID from the href (e.g., '#section-id' -> 'section-id')
            const sectionId = href.slice(1)
            const theme = themeMap[sectionId] || undefined

            return (
              <AnchorNav.Link key={link.sys.id} href={href} {...(theme ? {'data-theme': theme} : {})} {...analytics}>
                {link.fields.text}
              </AnchorNav.Link>
            )
          }

          return null // Don't render LinkGroups
        })}
      </AnchorNav>
    </section>
  )
}
